import { Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { useState, useEffect } from 'react';

import { ReactSelectForm, CustomSpinner } from '../../../components';

import { Accordion, Card } from 'react-bootstrap';
import { CustomAccordionHeader } from '../../tourDetails/common/components/CustomAccordionHeader';
import { useTranslation } from 'react-i18next';
import { useGetCurrentOrganization } from '../../../features/organizations/organizationsSelectors';
import { useQuery, useMutation } from '@tanstack/react-query';
import { getDuprClubIds, loginWithDupr, patchUserDuprId } from '../../../apis/dupr';
import { CustomButton } from '../../../components';
import { Notification } from '../../../hooks/useNotification';
import { TypeError } from '../../../types/enums';

export type DuprFormValues = {
  duprId: string;
};

type DuprFormProps = {
  duprFormRef: React.RefObject<FormikProps<DuprFormValues>>;
};

export const DuprForm = ({ duprFormRef }: DuprFormProps) => {
  const prefix = 'communitySettings-';
  const currentOrganization = useGetCurrentOrganization();
  const [loginUrl, setLoginUrl] = useState('');

  const { t } = useTranslation();

  const duprForm = {
    duprId: currentOrganization?.duprId || '',
  };
  const duprSchema = Yup.object().shape({
    duprId: Yup.string(),
  });

  const duprIds = useQuery({
    queryKey: ['dupr-ids'],
    queryFn: () => getDuprClubIds(),
  });

  const loginQuery = useQuery({
    queryKey: ['dupr-login'],
    queryFn: () => loginWithDupr(),
    enabled: false,
  });

  const updateDuprIdMutation = useMutation({
    mutationFn: (duprId: string) => patchUserDuprId(duprId),
    onSuccess: () => {
      // Refetch the DUPR club IDs to update the UI
      duprIds.refetch();
    },
    onError: (error) => {
      console.error('Error updating DUPR ID:', error);
      Notification(TypeError.error, t(prefix + 'duprUpdateError'));
    },
  });

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Verify the origin of the message
      if (event.origin !== 'https://dashboard.dupr.com') return;

      const { duprId } = event.data;
      if (duprId) {
        setLoginUrl('');
        updateDuprIdMutation.mutate(duprId);
      }
    };

    window.addEventListener('message', handleMessage, false);
    return () => window.removeEventListener('message', handleMessage);
  }, [updateDuprIdMutation]);

  const handleLoginClick = async () => {
    const result = await loginQuery.refetch();
    if (result.data?.loginUrl) {
      setLoginUrl(result.data.loginUrl);
    }
  };

  const displayDuprForm = () => {
    if (duprIds?.isPending) {
      return (
        <div className="d-flex justify-content-center align-items-center p-4">
          <CustomSpinner />
        </div>
      );
    }
    if (duprIds?.error) {
      return (
        <div className="alert alert-danger m-3">
          {t(prefix + 'error')}: {duprIds.error.message}
        </div>
      );
    }
    if (!duprIds?.data?.userDuprId) {
      return (
        <div className="d-flex flex-column align-items-center p-4">
          <p className="mb-3">{t(prefix + 'loginRequired')}</p>
          <CustomButton
            variant="primary"
            btnText={t(prefix + 'login')}
            onClick={handleLoginClick}
            disabled={loginQuery.isLoading || updateDuprIdMutation.isPending}
          >
            {loginQuery.isLoading || (updateDuprIdMutation.isPending && <CustomSpinner />)}
          </CustomButton>
          {loginUrl && (
            <div className="mt-3 w-100">
              <iframe
                src={loginUrl}
                style={{ width: '100%', height: '600px', border: 'none' }}
                title="DUPR Login"
              />
            </div>
          )}
        </div>
      );
    }

    if (duprIds?.data?.clubDuprIds.length === 0) {
      return <div className="alert alert-warning m-3">{t(prefix + 'noClubsFound')}</div>;
    }

    // Create base options from club IDs
    const baseOptions =
      duprIds?.data?.clubDuprIds.map((id) => ({
        value: id.clubId.toString(),
        label: id.clubName,
      })) || [];

    const unknownCurrentClub =
      !!currentOrganization?.duprId &&
      !baseOptions.some((opt) => opt.value === currentOrganization.duprId);

    // If current DUPR ID exists and isn't in the options, add it as an "Unknown Club" option
    const options =
      currentOrganization?.duprId && unknownCurrentClub
        ? [
            ...baseOptions,
            {
              value: currentOrganization.duprId,
              label: `Unknown Club (${currentOrganization.duprId})`,
            },
          ]
        : baseOptions;

    return (
      <>
        <Formik
          validationSchema={duprSchema}
          initialValues={duprForm}
          onSubmit={() => {}}
          innerRef={duprFormRef}
          enableReinitialize={true}
        >
          <Form>
            <div>
              {unknownCurrentClub && <p className="mb-3">{t(prefix + 'unknownCurrentClub')}</p>}
              <ReactSelectForm
                placeholder={t(prefix + 'selectDuprClub')}
                name="duprId"
                options={options}
                label={t(prefix + 'duprOrganization')}
              />
            </div>
          </Form>
        </Formik>
      </>
    );
  };

  return (
    <Accordion>
      <Card>
        <Card.Header className="p-0 bg-white border-0 relative">
          <CustomAccordionHeader eventKey="DUPR">{t(prefix + 'dupr')}</CustomAccordionHeader>
        </Card.Header>
        <Accordion.Collapse eventKey="DUPR">
          <Card.Body className="p-2">{displayDuprForm()}</Card.Body>
        </Accordion.Collapse>
      </Card>
    </Accordion>
  );
};
